import { useEffect, useState } from 'react';
import { plataforma } from '@/platform';

/**
 * Banner de atualização do app desktop. Ouve eventos do main process
 * via `plataforma.updater.aoEvento` e mostra o estado atual.
 *
 * No web o componente não renderiza nada: o navegador já carrega a versão
 * publicada a cada acesso, então não existe ciclo de atualização a expor.
 *
 * Estados visuais:
 *   - 'baixando'  → faixa azul com progresso
 *   - 'pronto'    → faixa verde com botão "Reiniciar agora"
 *   - 'erro'      → faixa amarela discreta (não bloqueia uso)
 *
 * 'checando' e 'nao-disponivel' não aparecem visualmente — são silenciosos
 * pra não distrair o usuário. O download é em background, sem prompt.
 */

type Estado =
  | { tipo: 'oculto' }
  | { tipo: 'baixando'; percentual: number; versao?: string }
  | { tipo: 'pronto'; versao: string }
  | { tipo: 'erro'; mensagem: string };

export function BannerAtualizacao() {
  const [estado, setEstado] = useState<Estado>({ tipo: 'oculto' });
  const [reiniciando, setReiniciando] = useState(false);

  useEffect(() => {
    if (!plataforma.capacidades.autoUpdate) return;
    const updater = plataforma.updater;
    if (!updater) return;
    let versaoCorrente: string | undefined;
    const unsubscribe = updater.aoEvento((ev) => {
      switch (ev.tipo) {
        case 'disponivel':
          versaoCorrente = ev.versao;
          setEstado({ tipo: 'baixando', percentual: 0, versao: ev.versao });
          break;
        case 'baixando':
          setEstado({
            tipo: 'baixando',
            percentual: ev.percentual,
            versao: versaoCorrente,
          });
          break;
        case 'pronto':
          setEstado({ tipo: 'pronto', versao: ev.versao });
          break;
        case 'erro':
          setEstado({ tipo: 'erro', mensagem: ev.mensagem });
          break;
        case 'nao-disponivel':
        case 'checando':
          // Silencioso.
          break;
      }
    });
    return () => unsubscribe();
  }, []);

  async function reiniciar() {
    setReiniciando(true);
    try {
      await plataforma.updater.instalarAgora();
    } catch {
      setReiniciando(false);
    }
  }

  if (estado.tipo === 'oculto') return null;

  if (estado.tipo === 'baixando') {
    return (
      <div
        role="status"
        style={{
          background: '#dbeafe',
          borderBottom: '1px solid #93c5fd',
          color: '#1e40af',
          padding: '8px 16px',
          fontSize: 13,
          textAlign: 'center',
        }}
      >
        <strong>Baixando atualização{estado.versao ? ` ${estado.versao}` : ''}</strong>
        <span style={{ marginLeft: 8 }}>
          {estado.percentual}% — você pode continuar trabalhando, o download é em background.
        </span>
      </div>
    );
  }

  if (estado.tipo === 'pronto') {
    return (
      <div
        role="status"
        style={{
          background: '#dcfce7',
          borderBottom: '1px solid #86efac',
          color: '#166534',
          padding: '8px 16px',
          fontSize: 13,
          textAlign: 'center',
          display: 'flex',
          gap: 12,
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <strong>Atualização {estado.versao} pronta.</strong>
        <span>Reinicie pra aplicar.</span>
        <button
          type="button"
          onClick={reiniciar}
          disabled={reiniciando}
          style={{
            background: '#16a34a',
            color: 'white',
            border: 'none',
            padding: '6px 14px',
            borderRadius: 6,
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          {reiniciando ? 'Reiniciando…' : 'Reiniciar agora'}
        </button>
      </div>
    );
  }

  // erro — discreto, não bloqueia uso
  return (
    <div
      role="status"
      style={{
        background: '#fef3c7',
        borderBottom: '1px solid #fde68a',
        color: '#92400e',
        padding: '6px 16px',
        fontSize: 12,
        textAlign: 'center',
      }}
    >
      Falha ao verificar atualização: {estado.mensagem}
    </div>
  );
}
